import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getRuns,
  getRun,
  createRun,
  cancelRun,
  deleteRun,
  getPages,
  getPage,
} from '@/lib/ingestion-api'
import type { CreatePipelineRunPayload, PipelineRun, PageItem } from '@/types/ingestion'

export type WsConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error'

const MAX_RECONNECT_ATTEMPTS = 10
const BASE_RECONNECT_DELAY = 1000
const MAX_RECONNECT_DELAY = 30000
const HEARTBEAT_INTERVAL = 30000

export function useIngestionWebSocket(runId: number | string) {
  const [connectionState, setConnectionState] = useState<WsConnectionState>('disconnected')
  const [runStatus, setRunStatus] = useState<Partial<PipelineRun> | null>(null)
  const [pageUpdates, setPageUpdates] = useState<Map<number, Partial<PageItem>>>(new Map())

  const socketRef = useRef<WebSocket | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const queryClient = useQueryClient()

  const cleanup = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = null
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (socketRef.current) {
      socketRef.current.onopen = null
      socketRef.current.onmessage = null
      socketRef.current.onclose = null
      socketRef.current.onerror = null
      if (
        socketRef.current.readyState === WebSocket.OPEN ||
        socketRef.current.readyState === WebSocket.CONNECTING
      ) {
        socketRef.current.close()
      }
      socketRef.current = null
    }
  }, [])

  const send = useCallback((data: unknown): boolean => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) return false
    try {
      socketRef.current.send(typeof data === 'string' ? data : JSON.stringify(data))
      return true
    } catch {
      return false
    }
  }, [])

  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current)
    heartbeatIntervalRef.current = setInterval(() => {
      send({ type: 'ping', timestamp: Date.now() })
    }, HEARTBEAT_INTERVAL)
  }, [send])

  const scheduleReconnect = useCallback(() => {
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      setConnectionState('error')
      return
    }
    reconnectAttemptsRef.current++
    const delay =
      Math.min(
        BASE_RECONNECT_DELAY * Math.pow(1.5, reconnectAttemptsRef.current - 1),
        MAX_RECONNECT_DELAY
      ) * (0.8 + Math.random() * 0.4)

    setConnectionState('connecting')
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null
      connectWs()
    }, delay)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data)
        // Consumer sends flat events: {type, run_id, status, ...} or {type, page_id, url, stage, ...}
        const type = message.type

        switch (type) {
          case 'run_status':
            setRunStatus(message)
            queryClient.invalidateQueries({ queryKey: ['ingestion-run', runId] })
            break
          case 'page_update': {
            const pageId = message.page_id ?? message.id
            if (pageId) {
              setPageUpdates((prev) => {
                const next = new Map(prev)
                next.set(pageId, { ...message, id: pageId })
                return next
              })
              queryClient.invalidateQueries({ queryKey: ['ingestion-pages', runId] })
            }
            break
          }
          case 'pong':
            break
          default:
            break
        }
      } catch {
        // ignore parse errors
      }
    },
    [runId, queryClient]
  )

  const connectWs = useCallback(() => {
    if (!runId) return
    cleanup()
    setConnectionState('connecting')

    const wsBaseUrl = import.meta.env.VITE_WS_URL
    let wsUrl: string
    if (wsBaseUrl) {
      wsUrl = `${wsBaseUrl}/ws/ingestion/runs/${String(runId)}/`
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const host = window.location.host
      wsUrl = `${protocol}//${host}/ws/ingestion/runs/${String(runId)}/`
    }

    try {
      const socket = new WebSocket(wsUrl)
      socketRef.current = socket

      socket.onopen = () => {
        reconnectAttemptsRef.current = 0
        setConnectionState('connected')
        startHeartbeat()
      }

      socket.onmessage = handleMessage

      socket.onclose = (event) => {
        cleanup()
        setConnectionState('disconnected')
        if (event.code !== 1000 && event.code !== 4003 && event.code !== 4004) {
          scheduleReconnect()
        }
      }

      socket.onerror = () => {
        // onclose fires after onerror
      }
    } catch {
      setConnectionState('disconnected')
      scheduleReconnect()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, cleanup, handleMessage, startHeartbeat, scheduleReconnect])

  useEffect(() => {
    if (runId) {
      connectWs()
    }
    return () => {
      cleanup()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId])

  return { connectionState, runStatus, pageUpdates }
}

export function useRuns() {
  return useQuery({
    queryKey: ['ingestion-runs'],
    queryFn: getRuns,
  })
}

export function useRun(runId: number | string, options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: ['ingestion-run', runId],
    queryFn: () => getRun(runId),
    enabled: !!runId,
    refetchInterval: options?.refetchInterval,
  })
}

export function useCreateRun() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: CreatePipelineRunPayload) => createRun(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-runs'] })
    },
  })
}

export function useCancelRun() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (runId: number | string) => cancelRun(runId),
    onSuccess: (_data, runId) => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-runs'] })
      queryClient.invalidateQueries({ queryKey: ['ingestion-run', runId] })
    },
  })
}

export function useDeleteRun() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (runId: number | string) => deleteRun(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingestion-runs'] })
    },
  })
}

export function usePages(runId: number | string, options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: ['ingestion-pages', runId],
    queryFn: () => getPages(runId),
    enabled: !!runId,
    refetchInterval: options?.refetchInterval,
  })
}

export function usePage(runId: number | string, pageId: number) {
  return useQuery({
    queryKey: ['ingestion-page', runId, pageId],
    queryFn: () => getPage(runId, pageId),
    enabled: !!runId && !!pageId,
  })
}
